import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import __ from "src/utils/Translations";
import { dynamicNotification } from "src/utils/Notifications";
import {
  changeStatusAbsenceType,
  createAbsenceType,
  editAbsenceType,
  fetchAbsenceType,
  fetchAbsenceTypeBalanceHistory,
  fetchAbsenceTypeHistory,
  fetchAbsenceTypes,
  saveCompanyMainAbsenceType,
} from "src/Pages/Company/Absences/api";
import {
  IAbsenceTypeBalanceHistory,
  IAbsenceType,
  IAbsenceTypeHistory,
} from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";

export const useListAbsenceTypes = (companyId: string) =>
  useQuery({
    queryKey: ["absence-types", "list", { companyId }],
    queryFn: async ({ signal }) => fetchAbsenceTypes(companyId, { signal }),
    initialData: [],
  });

export const useGetAbsenceType = (absenceTypeId: string | undefined) =>
  useQuery({
    enabled: !!absenceTypeId,
    queryKey: ["absence-types", "details", { absenceTypeId }],
    queryFn: async ({ signal }) => fetchAbsenceType({ absenceTypeId, signal }),
  });

export const useSaveAbsenceType = (
  absenceTypeId: string | undefined,
  fullyEditable: boolean
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (formData: IAbsenceType) => {
      const result = absenceTypeId
        ? await editAbsenceType(absenceTypeId, formData, fullyEditable)
        : await createAbsenceType(formData);
      return result;
    },
    onSuccess: (resultData) =>
      Promise.all([
        queryClient.setQueryData(
          ["absence-types", "details", { absenceTypeId: resultData.id }],
          resultData
        ),
        queryClient.invalidateQueries({
          queryKey: ["absence-types", "list"],
        }),
      ]),
  });
};

export const useChangeAbsenceTypeStatus = (companyId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      absenceTypeId,
      active,
    }: {
      absenceTypeId: string;
      active: boolean;
    }) => {
      const { data } = await changeStatusAbsenceType(absenceTypeId, active);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["absence-types"],
      });
    },
  });
};

export const useSetAbsenceTypeAsMain = (companyId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ absenceTypeId }: { absenceTypeId: string | null }) => {
      const { data } = await saveCompanyMainAbsenceType(
        companyId,
        absenceTypeId
      );

      return data;
    },
    onSuccess: () => {
      dynamicNotification(__("Pomyślnie zapisano zmianę."));
      queryClient.invalidateQueries({
        queryKey: ["absence-types"],
      });
    },
    onError: () => {
      dynamicNotification(__("Nie udało się zapisać."), "error");
    },
  });
};

export const useFetchAbsenceTypeHistory = (absenceTypeId: string) =>
  useQuery<IAbsenceTypeHistory, Error>({
    queryKey: ["absence-type-history", absenceTypeId],
    queryFn: () => fetchAbsenceTypeHistory(absenceTypeId),
    enabled: !!absenceTypeId,
  });

export const useFetchAbsenceTypeBalanceHistory = (params: {
  employeeId: string;
  absenceTypeId: string;
  page: number;
  size: number;
}) =>
  useQuery<IAbsenceTypeBalanceHistory, Error>({
    queryKey: ["absence-type-balance-history", params],
    queryFn: ({ signal }) =>
      fetchAbsenceTypeBalanceHistory({ ...params, signal }),
    enabled: !!params.absenceTypeId,
  });
